import { Box, Skeleton, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { StyledCard } from "./components/StyledComponents";
import RedirectIcon from "../../Components/Icons/RedirectIcon";
import InfoIconTooltip from "../../Components/Icons/InfoIconTooltip";
import CustomIcon from "../../Components/CustomIcon";
import ChangeIndicator from "./components/ChangeIndicator";
import { PrimaryTooltip } from "../../Components/StyledComponent";
import NonOperationalBreakdown from "./NonOperationalBreakdown";
import { getInventoryFilterUrl } from "./CommonFunctions";
import { useBaseEntityInfo } from "../../Hooks/usePagesContent";
import {
  formatCount,
  formatShare,
  getTotalVehiclesTileData,
  getMonthChangeTooltipLines,
  getTotalVehiclesTooltip,
} from "../../Helper/Overview/totalVehicles";

const inventoryLinkSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  color: "#686868",
  cursor: "pointer",
  transition: "color 0.3s ease",
  "&:hover": { color: "#4D44D8" },
};

const InventoryLink = ({ to, label, testId, children }) => (
  <Link
    to={to}
    style={{ display: "inline-flex", textDecoration: "none", minWidth: 0 }}
    rel="noopener noreferrer"
    target="_blank"
    data-testid={testId}
    aria-label={label}
  >
    <PrimaryTooltip title={label}>
      <Box component="span" sx={{ ...inventoryLinkSx, minWidth: 0 }}>
        {children}
        <RedirectIcon sx={{ fontSize: 12, color: "inherit", flexShrink: 0 }} />
      </Box>
    </PrimaryTooltip>
  </Link>
);

// Same tile as the Inventory status marker (FieldStatusIndicator), at row size.
const StatusBadge = ({ row }) => (
  <Box
    component="span"
    sx={{
      width: 18,
      height: 18,
      borderRadius: "5px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      backgroundColor: row.background,
      color: row.color,
    }}
  >
    <CustomIcon name={row.icon} size={11} />
  </Box>
);

const MONTH_CHANGE_COLORS = {
  up: { color: "#027A48", background: "#E7F6EC" },
  down: { color: "#B42318", background: "#FEE9E7" },
  none: { color: "#686868", background: "#F2F2F2" },
};

// Under the total: a change pill, then "vs N last month" on its own line.
const MonthChange = ({ data }) => {
  const { monthChange, monthChangePercentage, lastMonthTotal } = data;
  const { color, background } =
    MONTH_CHANGE_COLORS[monthChange > 0 ? "up" : monthChange < 0 ? "down" : "none"];
  const arrow = monthChange > 0 ? "\u2191 " : monthChange < 0 ? "\u2193 " : "";
  const percentage = monthChangePercentage === null ? "" : ` (${formatShare(monthChangePercentage)}%)`;
  const tooltipLines = getMonthChangeTooltipLines(data);

  return (
    <PrimaryTooltip
      placement="bottom"
      title={
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, textAlign: "left" }}>
          {tooltipLines.map((line) => (
            <span key={line}>{line}</span>
          ))}
        </Box>
      }
      componentsProps={{ tooltip: { sx: { maxWidth: 280 } } }}
    >
      <Box
        data-testid="total-vehicles-month-change"
        sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5, whiteSpace: "nowrap", cursor: "default" }}
      >
        <Typography
          variant="fw500s12c000"
          sx={{ color, backgroundColor: background, lineHeight: "16px", px: 1, py: "2px", borderRadius: "12px" }}
        >
          {monthChange === 0 ? "No change" : `${arrow}${formatCount(Math.abs(monthChange))}${percentage}`}
        </Typography>
        <Typography variant="fw500s12c686868" sx={{ lineHeight: "16px", fontWeight: 400 }}>
          vs {formatCount(lastMonthTotal)} last month
        </Typography>
      </Box>
    </PrimaryTooltip>
  );
};

const StatusBar = ({ rows, total }) => (
  <Box sx={{ display: "flex", gap: "2px", height: 4, borderRadius: "2px", overflow: "hidden", backgroundColor: "#F2F2F2" }}>
    {total > 0 &&
      rows
        .filter((row) => row.count > 0)
        .map((row) => (
          <PrimaryTooltip key={row.code} title={`${row.label}: ${formatCount(row.count)}`}>
            <Box sx={{ flexGrow: row.count, flexBasis: 0, minWidth: 4, backgroundColor: row.color }} />
          </PrimaryTooltip>
        ))}
  </Box>
);

// One grid for all rows, so count and change columns line up and size to content:
// badge | label + inventory arrow (+ breakdown) | count | change
const ROW_COLUMNS = "18px minmax(0, 1fr) auto auto";

const StatusRow = ({ row, globalFilters, entityPlural }) => {
  const inventoryUrl = getInventoryFilterUrl(globalFilters, { tag_battery_status: [row.filterValue] });

  return (
    <Box sx={{ display: "contents" }} data-testid={`total-vehicles-row-${row.code}`}>
      <PrimaryTooltip title={row.label}>
        <Box component="span" sx={{ display: "inline-flex" }}>
          <StatusBadge row={row} />
        </Box>
      </PrimaryTooltip>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
        <PrimaryTooltip title={row.getDefinition(entityPlural)} placement="top">
          <Typography variant="fw400s14c000000" noWrap sx={{ fontSize: "13px", lineHeight: "18px", minWidth: 0, cursor: "default" }}>
            {row.label}
          </Typography>
        </PrimaryTooltip>
        <InventoryLink
          to={inventoryUrl}
          label={`View ${row.label} in Inventory`}
          testId={`total-vehicles-inventory-${row.code}`}
        />
        {row.code === "non_operational" && (
          <Box sx={{ display: "inline-flex", flexShrink: 0, "& svg": { fontSize: 16 } }}>
            <NonOperationalBreakdown globalFilters={globalFilters} />
          </Box>
        )}
      </Box>
      <Typography variant="fw500s16lh20c000000" sx={{ fontSize: "14px", textAlign: "right" }}>
        {formatCount(row.count)}
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        {row.dayChange !== undefined ? (
          <ChangeIndicator
            count={row.dayChange}
            percentage={row.dayChangePercentage}
            positiveWhenIncrease={row.positiveWhenIncrease}
          />
        ) : (
          <PrimaryTooltip title="Share of total">
            <Typography variant="fw500s12c686868" sx={{ lineHeight: "18px", cursor: "default" }}>
              {formatShare(row.share)}%
            </Typography>
          </PrimaryTooltip>
        )}
      </Box>
    </Box>
  );
};

const TotalVehiclesCard = ({ stats, globalFilters, loading }) => {
  const { baseEntityTypePluralLable } = useBaseEntityInfo();
  const data = getTotalVehiclesTileData(stats);

  const allStatusesInventoryUrl = getInventoryFilterUrl(globalFilters, {
    tag_battery_status: (data?.rows || []).map((row) => row.filterValue),
  });

  return (
    // border-box: StyledCard is width 100% with padding, which otherwise overflows its slot.
    <StyledCard sx={{ boxSizing: "border-box", minWidth: 0, display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
          <Typography variant="fw500s16lh20c000000" noWrap>
            Total {baseEntityTypePluralLable}
          </Typography>
          <InfoIconTooltip
            title={getTotalVehiclesTooltip(baseEntityTypePluralLable, data?.hasOnboardingStatus)}
            maxWidth={240}
          />
        </Box>
        <InventoryLink to={allStatusesInventoryUrl} label="View in Inventory" testId="total-vehicles-inventory">
          <Typography variant="fw500s12c686868" sx={{ lineHeight: "18px", color: "inherit" }}>
            Inventory
          </Typography>
        </InventoryLink>
      </Box>

      {loading ? (
        <>
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 2 }}>
            <Skeleton variant="rounded" width={96} height={76} />
            <Skeleton variant="rounded" height={76} sx={{ flex: 1 }} />
          </Box>
          <Skeleton variant="rounded" height={4} />
        </>
      ) : !data ? (
        <Typography variant="fw500s20c000000" sx={{ fontSize: "24px", lineHeight: "32px" }}>
          -
        </Typography>
      ) : (
        <>
          {/* Two columns: total + month change | status rows. Grows to the row height so the bar sits at the bottom. */}
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                flexShrink: 0,
                pr: 2,
                borderRight: "1px solid #E4E4E4",
              }}
            >
              <Typography variant="fw500s20c000000" sx={{ fontSize: "32px", lineHeight: "36px" }}>
                {formatCount(data.total)}
              </Typography>
              {data.hasLastMonth && <MonthChange data={data} />}
            </Box>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "grid",
                gridTemplateColumns: ROW_COLUMNS,
                alignItems: "center",
                columnGap: 0.75,
                rowGap: 1,
              }}
            >
              {data.rows.map((row) => (
                <StatusRow
                  key={row.code}
                  row={row}
                  globalFilters={globalFilters}
                  entityPlural={baseEntityTypePluralLable}
                />
              ))}
            </Box>
          </Box>
          <StatusBar rows={data.rows} total={data.total} />
        </>
      )}
    </StyledCard>
  );
};

export default TotalVehiclesCard;
